import { useIsMobile } from "@/hooks/use-mobile";
import { CreationOverlay } from "../misc/CreationOverlay";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SalesListMobile } from "./SalesListMobile";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CanAccess,
  Navigate,
  useDataProvider,
  useNotify,
  useRedirect,
  useTranslate,
} from "ra-core";
import type { SubmitHandler } from "react-hook-form";
import { SimpleForm } from "@/components/admin/simple-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { CrmDataProvider } from "../providers/types";
import type { SalesFormData } from "../types";
import { getSalesErrorNotification } from "./salesErrorNotification";
import { SalesInputs } from "./SalesInputs";

export function SalesCreate() {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const translate = useTranslate();
  const redirect = useRedirect();
  const mobile = useIsMobile();

  const { mutate } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (data: SalesFormData) => {
      return dataProvider.salesCreate(data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["sales"] });
      notify(
        "Пользователь создан, приглашение передано почтовому серверу. Проверьте входящие и спам.",
        { type: "success" },
      );
      redirect("/sales");
    },
    onError: (error) => {
      const { message, args } = getSalesErrorNotification(
        error,
        error.message ||
          translate("resources.sales.create.error", {
            _: "An error occurred while creating the user.",
          }),
      );
      notify(message, { type: "error", messageArgs: args });
    },
  });
  const onSubmit: SubmitHandler<SalesFormData> = async (data) => {
    mutate(data);
  };

  const form = (
    <SimpleForm onSubmit={onSubmit as SubmitHandler<any>}>
      <SalesInputs />
    </SimpleForm>
  );

  return (
    <CanAccess
      resource="sales"
      action="create"
      accessDenied={<Navigate to="/access-denied" />}
    >
      {mobile ? (
        <>
          <SalesListMobile />
          <CreationOverlay
            open
            onOpenChange={(open) => {
              if (!open) redirect("/sales");
            }}
          >
            <SheetHeader className="px-4 py-4 border-b shrink-0">
              <SheetTitle>Добавить пользователя</SheetTitle>
            </SheetHeader>
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-5">
              {form}
            </div>
          </CreationOverlay>
        </>
      ) : (
        <div className="max-w-lg w-full mx-auto mt-8">
          <Card>
            <CardHeader>
              <CardTitle>
                {translate("resources.sales.create.title", {
                  _: "Create a new user",
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>{form}</CardContent>
          </Card>
        </div>
      )}
    </CanAccess>
  );
}
